import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  VpnKey as VpnKeyIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import { useFormLoteFinanciera } from "../../hooks/useFormLoteFinanciera";
import ConfirmacionImportacionLoteFinanciera from "../Confirmacion/ConfirmacionImportacionLoteFinanciera";

const colors = {
  primary: "#0b4f6c",
  success: "#27ae60",
  error: "#e74c3c",
  info: "#2980b9",
  background: "#f8f9fa",
  border: "#dfe6e9",
  warning: "#ed6c02",
};

const ModalSubirDocumento = ({
  open,
  onClose,
  onImportSuccess,
  onError,
  idConcil,
  keyConcil,
  code,
  conciliationName,
  vertical,
}) => {
  const {
    selectedFiles,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    selectedLlave,
    llaveOptions,
    loadingKeys,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
    handleLlaveChange,
  } = useFormLoteFinanciera(idConcil, code, keyConcil);

  const [showErrorDetails, setShowErrorDetails] = React.useState(true);

  const onFileSelect = async (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      await handleFileChange(fileArray);
    }
  };

  const onImportClick = async () => {
    await handleImport(onImportSuccess, onError);
  };

  const hasErrors =
    validationErrors.length > 0 || duplicateInFile.length > 0 || duplicateInDatabase.length > 0;
  const isValidFile = selectedFiles.length > 0 && !hasErrors && !validating && selectedLlave;

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    if (extension === "pdf") {
      return <PictureAsPdfIcon sx={{ color: colors.error, fontSize: 20 }} />;
    }
    return <ImageIcon sx={{ color: colors.info, fontSize: 20 }} />;
  };

  const getFileExtension = (fileName) => {
    return fileName.split(".").pop().toUpperCase();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "1100px",
            maxWidth: "calc(100% - 32px)",
            m: 2,
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            px: 4,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: colors.primary,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CloudUploadIcon fontSize="large" sx={{ color: "#fff !important" }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color="white">
                Subir Documento
              </MDTypography>
              {conciliationName && (
                <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                  {conciliationName}
                </MDTypography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="large" sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 4 }, pt: "24px !important", overflow: "auto" }}>
          <Grid container spacing={3}>
            {/* Información de la conciliación */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f0f7ff",
                  borderRadius: 2,
                  border: `1px solid ${colors.info}30`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <VpnKeyIcon sx={{ color: colors.info, fontSize: 20 }} />
                  <MDTypography variant="body2" fontWeight="bold" color={colors.info}>
                    Información de la Conciliación
                  </MDTypography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  {keyConcil && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <MDTypography variant="caption" color="text.secondary">
                        Llave:
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {keyConcil}
                      </MDTypography>
                    </Box>
                  )}
                  {idConcil && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <MDTypography variant="caption" color="text.secondary">
                        ID:
                      </MDTypography>
                      <MDTypography variant="body2" fontWeight="bold">
                        {idConcil}
                      </MDTypography>
                    </Box>
                  )}
                  {code && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <MDTypography variant="caption" color="text.secondary">
                        Concesión:
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {code}
                      </MDTypography>
                    </Box>
                  )}
                  {vertical && (
                    <Chip
                      label={vertical}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        backgroundColor: `${colors.success}15`,
                        color: colors.success,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Select de Llave de Conciliación (solo si no viene prefijada) */}
            {!keyConcil && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Llave de Conciliación *"
                  value={selectedLlave || ""}
                  onChange={handleLlaveChange}
                  variant="outlined"
                  disabled={validating || loadingKeys}
                  InputProps={{
                    startAdornment: selectedLlave && (
                      <InputAdornment position="start">
                        <VpnKeyIcon sx={{ color: colors.info, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                      fontSize: "1rem",
                      backgroundColor: selectedLlave ? "#f0f7ff" : "white",
                      borderRadius: 2,
                    },
                  }}
                >
                  {loadingKeys ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Cargando llaves...
                    </MenuItem>
                  ) : llaveOptions.length === 0 ? (
                    <MenuItem disabled>No hay llaves disponibles</MenuItem>
                  ) : (
                    llaveOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                          py: 1.5,
                          px: 2,
                          "&:hover": { backgroundColor: "#f0f7ff" },
                          "&.Mui-selected": {
                            backgroundColor: `${colors.info}15`,
                            "&:hover": { backgroundColor: `${colors.info}25` },
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <VpnKeyIcon sx={{ color: colors.info, fontSize: 18 }} />
                          <MDTypography
                            variant="body2"
                            fontWeight={selectedLlave === option.value ? "bold" : "regular"}
                            sx={{ fontFamily: "monospace" }}
                          >
                            {option.label}
                          </MDTypography>
                          {selectedLlave === option.value && (
                            <CheckCircleIcon sx={{ color: colors.success, fontSize: 16, ml: 1 }} />
                          )}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
            )}

            {/* Área de carga de archivos */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `2px dashed ${
                    isValidFile
                      ? colors.success
                      : hasErrors && selectedFiles.length > 0
                      ? colors.error
                      : !selectedLlave && selectedFiles.length > 0
                      ? colors.warning
                      : colors.border
                  }`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  backgroundColor: isValidFile
                    ? `${colors.success}08`
                    : hasErrors && selectedFiles.length > 0
                    ? `${colors.error}08`
                    : !selectedLlave && selectedFiles.length > 0
                    ? `${colors.warning}08`
                    : colors.background,
                  transition: "all 0.2s",
                  cursor: selectedLlave ? "pointer" : "not-allowed",
                  opacity: selectedLlave ? 1 : 0.7,
                  "&:hover": {
                    borderColor: selectedLlave
                      ? isValidFile
                        ? colors.success
                        : hasErrors && selectedFiles.length > 0
                        ? colors.error
                        : colors.info
                      : colors.border,
                    backgroundColor: selectedLlave
                      ? isValidFile
                        ? `${colors.success}10`
                        : hasErrors && selectedFiles.length > 0
                        ? `${colors.error}10`
                        : "#fff"
                      : colors.background,
                  },
                }}
                onClick={() => {
                  if (selectedLlave) {
                    document.getElementById("file-input-pdf-modal")?.click();
                  } else {
                    setSnackbar({
                      open: true,
                      message: "⚠️ Seleccione una llave de conciliación primero",
                      severity: "error",
                    });
                  }
                }}
              >
                <input
                  id="file-input-pdf-modal"
                  type="file"
                  accept=".pdf,.jpeg,.jpg,.png"
                  multiple
                  onChange={onFileSelect}
                  style={{ display: "none" }}
                  disabled={validating || !selectedLlave}
                />
                {validating ? (
                  <>
                    <CircularProgress size={48} sx={{ mb: 1 }} />
                    <MDTypography variant="h6" color={colors.info} gutterBottom>
                      Validando archivos...
                    </MDTypography>
                  </>
                ) : selectedFiles.length > 0 ? (
                  <>
                    {isValidFile ? (
                      <CheckCircleIcon sx={{ fontSize: 48, color: colors.success, mb: 1 }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 48, color: colors.error, mb: 1 }} />
                    )}
                    <MDTypography
                      variant="h6"
                      color={isValidFile ? colors.success : colors.error}
                      gutterBottom
                    >
                      {isValidFile
                        ? `${selectedFiles.length} archivo(s) válido(s)`
                        : "Archivos inválidos"}
                    </MDTypography>

                    <Box sx={{ mt: 2, textAlign: "left", maxHeight: 200, overflow: "auto" }}>
                      {selectedFiles.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1,
                            mb: 0.5,
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            {getFileIcon(file.name)}
                            <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
                              {file.name}
                            </MDTypography>
                            <Chip
                              label={getFileExtension(file.name)}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 20,
                                bgcolor: file.name.toLowerCase().endsWith("pdf")
                                  ? "#ffebee"
                                  : "#e3f2fd",
                                color: file.name.toLowerCase().endsWith("pdf")
                                  ? colors.error
                                  : colors.info,
                              }}
                            />
                            <MDTypography variant="caption" color="text.secondary">
                              ({Math.round(file.size / 1024)} KB)
                            </MDTypography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newFiles = selectedFiles.filter((_, i) => i !== index);
                              if (newFiles.length === 0) {
                                clearSelectedFile();
                              } else {
                                const dataTransfer = new DataTransfer();
                                newFiles.forEach((f) => dataTransfer.items.add(f));
                                const fileInput = document.getElementById("file-input-pdf-modal");
                                if (fileInput) fileInput.files = dataTransfer.files;
                                handleFileChange(newFiles);
                              }
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16, color: colors.error }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <MDTypography variant="caption" color="text.secondary" display="block">
                        ({selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024} MB total)
                        - Llave: {selectedLlave}
                      </MDTypography>
                      {idConcil && (
                        <MDTypography variant="caption" color="text.secondary" display="block">
                          ID Conciliación: <strong>{idConcil}</strong>
                        </MDTypography>
                      )}
                      {code && (
                        <MDTypography variant="caption" color="text.secondary" display="block">
                          Concesión: <strong>{code}</strong>
                        </MDTypography>
                      )}
                    </Box>
                    <MDButton
                      variant="text"
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSelectedFile();
                      }}
                      sx={{ mt: 1 }}
                    >
                      Limpiar todos los archivos
                    </MDButton>
                  </>
                ) : (
                  <>
                    <DescriptionIcon
                      sx={{
                        fontSize: 64,
                        color: !selectedLlave ? colors.warning : colors.info,
                        mb: 1,
                      }}
                    />
                    <MDTypography
                      variant="h6"
                      color={!selectedLlave ? colors.warning : colors.info}
                      gutterBottom
                    >
                      {!selectedLlave ? "Seleccione una llave primero" : "Seleccionar archivos"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      {!selectedLlave
                        ? "Elija una llave de conciliación para habilitar la carga"
                        : "Haga clic para buscar archivos PDF, JPEG o PNG con las imágenes de los pagos"}
                    </MDTypography>
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        icon={<PictureAsPdfIcon />}
                        label="PDF"
                        size="small"
                        sx={{ bgcolor: "#ffebee", color: colors.error }}
                      />
                      <Chip
                        icon={<ImageIcon />}
                        label="JPEG / PNG"
                        size="small"
                        sx={{ bgcolor: "#e3f2fd", color: colors.info }}
                      />
                      <Chip label="Máx. 50MB c/u" size="small" sx={{ bgcolor: "#f5f5f5" }} />
                      <Chip
                        label="Múltiples archivos"
                        size="small"
                        sx={{ bgcolor: "#e8f5e9", color: colors.success }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Mostrar errores de validación */}
          {validationErrors.length > 0 && selectedFiles.length > 0 && (
            <Collapse in={showErrorDetails}>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#ffebee",
                  borderRadius: 2,
                  border: "1px solid #ef9a9a",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#c62828",
                    color: "white",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorIcon />
                    <MDTypography variant="subtitle2" fontWeight="bold" color="white">
                      Errores de validación ({validationErrors.length})
                    </MDTypography>
                    <MDButton
                      size="small"
                      variant="text"
                      sx={{ color: "white", ml: 2 }}
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                    >
                      {showErrorDetails ? "Ocultar" : "Mostrar"}
                    </MDButton>
                  </Box>
                </Box>
                <Box sx={{ p: 2, maxHeight: 200, overflow: "auto" }}>
                  <List dense disablePadding>
                    {validationErrors.slice(0, 10).map((err, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CancelIcon sx={{ fontSize: 16, color: colors.error }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={err}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#c62828", fontFamily: "monospace" },
                          }}
                        />
                      </ListItem>
                    ))}
                    {validationErrors.length > 10 && (
                      <ListItem>
                        <ListItemText
                          primary={`... y ${validationErrors.length - 10} errores más`}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#c62828", fontStyle: "italic" },
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Mensaje cuando los archivos son válidos */}
          {isValidFile && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#e8f5e9",
                borderRadius: 2,
                border: "1px solid #a5d6a7",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleIcon sx={{ color: colors.success }} />
                <MDTypography variant="subtitle2" fontWeight="bold" color={colors.success}>
                  Validación exitosa - {selectedFiles.length} archivo(s) para la llave{" "}
                  {selectedLlave}
                </MDTypography>
              </Box>
              <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Los archivos están listos para ser subidos.
              </MDTypography>
              {idConcil && (
                <MDTypography variant="caption" color="text.secondary" display="block">
                  ID Conciliación: <strong>{idConcil}</strong>
                </MDTypography>
              )}
              {code && (
                <MDTypography variant="caption" color="text.secondary" display="block">
                  Concesión: <strong>{code}</strong>
                </MDTypography>
              )}
            </Box>
          )}

          {/* Advertencia si no hay llave seleccionada */}
          {!selectedLlave && selectedFiles.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#fff3e0",
                borderRadius: 2,
                border: "1px solid #ffe082",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon sx={{ color: colors.warning }} />
                <MDTypography variant="subtitle2" fontWeight="bold" color={colors.warning}>
                  Seleccione una llave de conciliación
                </MDTypography>
              </Box>
              <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Debe seleccionar una llave de conciliación para poder subir los archivos.
              </MDTypography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: 1, borderColor: "divider" }}>
          <MDButton variant="outlined" onClick={onClose} disabled={importing}>
            Cancelar
          </MDButton>
          <MDButton
            variant="contained"
            disabled={!isValidFile || importing || validating || !selectedLlave}
            onClick={onImportClick}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: "bold",
              color: "#ffffffff",
              borderRadius: 1,
              bgcolor: isValidFile && selectedLlave ? colors.success : colors.border,
              "&:hover": {
                bgcolor: isValidFile && selectedLlave ? "#1e8449" : colors.border,
              },
              "&:disabled": {
                bgcolor: colors.border,
                color: "white",
              },
            }}
          >
            {importing ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1.5, color: "white" }} />
                Subiendo {selectedFiles.length} archivo(s)...
              </>
            ) : validating ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1.5, color: "white" }} />
                Validando...
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ mr: 1.5 }} />
                Subir{" "}
                {selectedFiles.length > 0 ? `${selectedFiles.length} archivo(s)` : "Documento"}
              </>
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      <ConfirmacionImportacionLoteFinanciera
        open={showConfirmModal}
        onClose={handleCloseModal}
        importing={importing}
        importResult={importResult}
        fileName={selectedFiles.length > 0 ? `${selectedFiles.length} archivo(s)` : ""}
        importProgress={importProgress}
        llave={selectedLlave}
        filesCount={selectedFiles.length}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "info" ? 3000 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{
            width: "100%",
            minWidth: "300px",
            backgroundColor:
              snackbar.severity === "success"
                ? "#f0fff4"
                : snackbar.severity === "error"
                ? "#fff5f5"
                : snackbar.severity === "info"
                ? "#e3f2fd"
                : "#fff8e1",
            color:
              snackbar.severity === "success"
                ? "#276749"
                : snackbar.severity === "error"
                ? "#c53030"
                : snackbar.severity === "info"
                ? "#0b4f6c"
                : "#856404",
            "& .MuiAlert-icon": {
              color:
                snackbar.severity === "success"
                  ? "#276749"
                  : snackbar.severity === "error"
                  ? "#c53030"
                  : snackbar.severity === "info"
                  ? "#0b4f6c"
                  : "#856404",
            },
            border:
              snackbar.severity === "success"
                ? "1px solid #c6f6d5"
                : snackbar.severity === "error"
                ? "1px solid #fed7d7"
                : snackbar.severity === "info"
                ? "1px solid #b3e5fc"
                : "1px solid #ffe082",
          }}
        >
          <MDTypography variant="body1" fontWeight="bold">
            {snackbar.severity === "success"
              ? "¡Éxito!"
              : snackbar.severity === "error"
              ? "Error"
              : snackbar.severity === "info"
              ? "Información"
              : "Aviso"}
          </MDTypography>
          <MDTypography variant="body2">{snackbar.message}</MDTypography>
          {snackbar.severity === "info" && importProgress > 0 && importProgress < 100 && (
            <LinearProgress
              variant="determinate"
              value={importProgress}
              sx={{ mt: 1, height: 4, borderRadius: 2 }}
            />
          )}
        </Alert>
      </Snackbar>
    </>
  );
};

ModalSubirDocumento.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImportSuccess: PropTypes.func,
  onError: PropTypes.func,
  idConcil: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  keyConcil: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  conciliationName: PropTypes.string,
  vertical: PropTypes.string,
};

ModalSubirDocumento.defaultProps = {
  onImportSuccess: null,
  onError: null,
  idConcil: null,
  keyConcil: null,
  code: null,
  conciliationName: null,
  vertical: null,
};

export default ModalSubirDocumento;
